import { gql } from "@apollo/client";
import type { EpocusReviewSyncPushDocument } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { EpocusReview } from "../collections/epocus-review";
import type { RxDBCollections } from "../provider";
import {
  SyncEpocusReviewPushDocument,
  type WholeSyncableEpocusReviewFragment,
} from "./__generated__/epocus-review.gql";
import { useGqlReplication } from "./use-gql-replication";

export const useReplicateEpocusReview = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<EpocusReview>;
}) => {
  const push = useMemo(
    () => ({
      mutation: SyncEpocusReviewPushDocument,
      mapRxToGql: mapRxEpocusReviewToGqlInput,
    }),
    [],
  );

  // Pull requires an examId filter — not available in the global context.
  // Review data is created locally and pushed to the server; pull is handled in context.
  return useGqlReplication<
    EpocusReview,
    WholeSyncableEpocusReviewFragment,
    EpocusReviewSyncPushDocument
  >({
    db,
    collection,
    push,
    mapGqlToRx: mapGqlEpocusReviewToRx,
    batchSize: 100,
    expireData: false,
    metadataLocalStorageKeySuffix: "epocus-review",
  });
};

export const mapGqlEpocusReviewToRx = (
  doc: WholeSyncableEpocusReviewFragment,
): WithDeleted<EpocusReview> => {
  const mapped = {
    id: doc.id,
    examId: doc.examId,
    reviewerId: doc.meta.createdByUserId,
    agreement: doc.agree ? "agree" : "disagree",
    qualityScore: doc.qualityScore,
    feedbackTags: [...(doc.tags ?? [])],
    comment: doc.comment ?? null,
    // originalFindings and revisedFindings are local-only fields not returned by the
    // server. Because mapGqlToRx is also called on push response rows, we intentionally
    // omit these fields here so that existing local values are preserved in RxDB.
    meta: {
      createdAt: doc.meta.createdAt,
      updatedAt: doc.meta.updatedAt,
      createdByUserId: doc.meta.createdByUserId,
      updatedByUserId: doc.meta.updatedByUserId,
    },
    _deleted: doc.meta.deleted,
  };

  return mapped as WithDeleted<EpocusReview>;
};

export const mapRxEpocusReviewToGqlInput = (
  doc: WithDeleted<EpocusReview>,
): EpocusReviewSyncPushDocument => ({
  id: doc.id,
  examId: doc.examId,
  agree: doc.agreement === "agree",
  qualityScore: doc.qualityScore,
  tags: doc.feedbackTags,
  comment: doc.comment,
  meta: {
    createdAt: doc.meta.createdAt,
    updatedAt: doc.meta.updatedAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedByUserId: doc.meta.updatedByUserId,
    deleted: doc._deleted,
  },
});

gql`
  fragment WholeSyncableEpocusReview on SyncableEpocusReview {
    id
    examId
    agree
    qualityScore
    tags
    comment
    meta {
      createdAt
      updatedAt
      createdByUserId
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncEpocusReviewPull($input: PullEpocusReviewInput!) {
    pullEpocusReview(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableEpocusReview
      }
    }
  }
`;

gql`
  mutation SyncEpocusReviewPush($input: PushEpocusReviewInput!) {
    pushEpocusReview(input: $input) {
      rows {
        ...WholeSyncableEpocusReview
      }
    }
  }
`;
